import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { lieuxApi } from '../api/lieuxApi';
import { PUBLIC_FORM, greyAlpha } from '../theme/tones';

/**
 * Grille d'évaluation d'un lieu, remplie par le lieu lui-même.
 *
 * Même questionnaire que l'onglet « Évaluation » d'une fiche lieu
 * (FOR-DR-003, onze critères de conformité), mais côté exploitant : il arrive
 * ici par le lien reçu par courriel, sans compte ni mot de passe. Le jeton de
 * l'URL est sa seule preuve d'identité — il désigne un lieu et un seul.
 *
 * Sa réponse n'écrase pas l'évaluation interne : elle vit à côté, et l'onglet
 * montre les deux regards.
 */

/** Carte blanche du formulaire : le même filet que les autres pages publiques. */
const surfaceCard = {
  backgroundColor: PUBLIC_FORM.surface,
  border: `1px solid ${PUBLIC_FORM.line}`,
  borderRadius: '14px',
  boxShadow: `0 1px 2px ${greyAlpha(900, 0.04)}, 0 8px 24px -16px ${greyAlpha(900, 0.16)}`
};

/** Fond commun à tous les états de la page. */
const pageBackground = {
  minHeight: '100vh',
  backgroundColor: PUBLIC_FORM.canvas,
  backgroundImage: `radial-gradient(700px 320px at 15% -5%, ${PUBLIC_FORM.accentSoft}, transparent 60%)`,
  fontFamily: PUBLIC_FORM.font
};

/**
 * Les deux réponses possibles.
 *
 * Mêmes codes que l'évaluation interne (`conforme` / `non_conforme`) : les
 * deux grilles se notent avec la même règle, et un troisième code ici
 * fausserait la comparaison.
 */
const CHOIX = [
  { code: 'conforme', label: 'Oui', tone: '#059669', soft: 'rgba(5, 150, 105, 0.1)' },
  { code: 'non_conforme', label: 'Non', tone: '#e11d48', soft: 'rgba(225, 29, 72, 0.1)' }
];

/** Un écran d'un seul message : chargement raté, lien mort, ou merci. */
const Message = ({ titre, texte, liseret, enfant }) => (
  <Box display="flex" justifyContent="center" alignItems="flex-start" sx={{ ...pageBackground, pt: 8 }}>
    <Container maxWidth="sm">
      <Paper elevation={0} sx={{ ...surfaceCard, overflow: 'hidden' }}>
        <Box sx={{ height: 3, background: liseret }} />
        <Box sx={{ p: 5, textAlign: 'center' }}>
          {enfant}
          <Typography
            sx={{
              fontFamily: PUBLIC_FORM.fontDisplay,
              fontSize: '1.5rem',
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: PUBLIC_FORM.text
            }}
          >
            {titre}
          </Typography>
          <Typography sx={{ color: PUBLIC_FORM.textMuted, fontSize: '0.9rem', mt: 1.5, lineHeight: 1.6 }}>
            {texte}
          </Typography>
        </Box>
      </Paper>
    </Container>
  </Box>
);

const EvaluationLieuPublic = () => {
  const { token } = useParams();
  const [chargement, setChargement] = useState(true);
  const [criteres, setCriteres] = useState({});
  const [lieu, setLieu] = useState(null);
  const [reponses, setReponses] = useState({});
  const [repondant, setRepondant] = useState('');
  const [fonction, setFonction] = useState('');
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [envoye, setEnvoye] = useState(false);

  useEffect(() => {
    const charger = async () => {
      try {
        const res = await lieuxApi.publicAudit(token);
        if (!res || res.status !== 200) {
          setErreur((res && res.message) || 'Ce lien n’est plus valable.');
          return;
        }
        setCriteres(res.criteres || {});
        setLieu(res.data.lieu || null);
        // Le lieu peut revenir corriger sa grille : on repart de sa saisie.
        setReponses(res.data.reponses || {});
        setRepondant(res.data.repondant || '');
        setFonction(res.data.fonction || '');
      } catch (err) {
        setErreur('Le formulaire n’a pas pu être chargé.');
      } finally {
        setChargement(false);
      }
    };
    charger();
  }, [token]);

  const poser = (rang, champ, valeur) => {
    setReponses((prev) => ({
      ...prev,
      [rang]: { ...(prev[rang] || {}), [champ]: valeur }
    }));
  };

  const envoyer = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    try {
      const res = await lieuxApi.publicAuditSubmit(token, {
        criteres: reponses,
        repondant,
        fonction
      });
      if (!res || res.status !== 200) {
        setErreur((res && res.message) || 'L’envoi a échoué.');
        return;
      }
      setEnvoye(true);
    } catch (err) {
      setErreur('Vos réponses n’ont pas pu être envoyées.');
    } finally {
      setEnvoi(false);
    }
  };

  if (chargement) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={pageBackground}>
        <CircularProgress sx={{ color: PUBLIC_FORM.accent }} />
      </Box>
    );
  }

  if (erreur && !lieu) {
    return <Message titre="Une difficulté est survenue" texte={erreur} liseret={PUBLIC_FORM.danger} />;
  }

  if (envoye) {
    return (
      <Message
        titre="Merci pour votre retour"
        texte="Votre grille a bien été enregistrée. Vous pouvez fermer cette page."
        liseret={PUBLIC_FORM.aurora}
        enfant={(
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              mx: 'auto',
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: PUBLIC_FORM.accentSoft
            }}
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={PUBLIC_FORM.accent} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12.5 L9.5 18 L20 6.5" />
            </svg>
          </Box>
        )}
      />
    );
  }

  const rangs = Object.keys(criteres).sort((a, b) => Number(a) - Number(b));
  const repondus = rangs.filter((r) => reponses[r]
    && (reponses[r].statut === 'conforme' || reponses[r].statut === 'non_conforme')).length;
  const progression = rangs.length > 0 ? Math.round((repondus / rangs.length) * 100) : 0;
  const adresse = lieu
    ? [lieu.adresse, lieu.ville].filter(Boolean).join(', ')
    : '';

  return (
    <Box sx={{ ...pageBackground, py: 5 }}>
      <Container maxWidth="sm">
        <form onSubmit={envoyer}>

          {/* ------------------------------------------------ En-tête -- */}
          <Paper elevation={0} sx={{ ...surfaceCard, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ height: 3, background: PUBLIC_FORM.aurora }} />
            <Box sx={{ p: 3.5 }}>
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: PUBLIC_FORM.accent
                }}
              >
                Grille d’évaluation du lieu
              </Typography>
              <Typography
                sx={{
                  fontFamily: PUBLIC_FORM.fontDisplay,
                  fontSize: '1.4rem',
                  lineHeight: 1.25,
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: PUBLIC_FORM.text,
                  mt: 1.25
                }}
              >
                {lieu && lieu.nom}
              </Typography>
              {adresse && (
                <Typography sx={{ fontSize: '0.82rem', color: PUBLIC_FORM.textMuted, mt: 0.5 }}>
                  {adresse}
                </Typography>
              )}
              <Typography sx={{ fontSize: '0.85rem', color: PUBLIC_FORM.textMuted, mt: 2, lineHeight: 1.6 }}>
                Onze points d’accessibilité, de conformité et d’équipement.
                Répondez par oui ou non, et précisez si nécessaire.
              </Typography>
            </Box>
          </Paper>

          {/* --------------------------------------------- Progression -- */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, px: 0.5, mb: 2 }}>
            <Box
              sx={{
                flex: 1,
                height: 6,
                borderRadius: '999px',
                backgroundColor: PUBLIC_FORM.lineStrong,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  width: `${progression}%`,
                  height: '100%',
                  borderRadius: '999px',
                  background: PUBLIC_FORM.cta,
                  transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              />
            </Box>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: PUBLIC_FORM.textMuted, whiteSpace: 'nowrap' }}>
              {repondus} sur {rangs.length}
            </Typography>
          </Box>

          {/* ----------------------------------------------- Critères -- */}
          {rangs.map((rang, index) => {
            const rep = reponses[rang] || {};
            return (
              <Paper
                key={rang}
                elevation={0}
                sx={{
                  ...surfaceCard,
                  p: 3,
                  mb: 2,
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  '&:focus-within': {
                    borderColor: PUBLIC_FORM.accent,
                    boxShadow: `0 0 0 4px ${PUBLIC_FORM.accentSoft}`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      flexShrink: 0,
                      borderRadius: '8px',
                      backgroundColor: PUBLIC_FORM.accentSoft,
                      color: PUBLIC_FORM.accentDark,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: PUBLIC_FORM.text, lineHeight: 1.5 }}>
                    {criteres[rang]}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', mb: 2 }}>
                  {CHOIX.map((choix) => {
                    const actif = rep.statut === choix.code;
                    return (
                      <Box
                        key={choix.code}
                        component="button"
                        type="button"
                        onClick={() => poser(rang, 'statut', actif ? '' : choix.code)}
                        sx={{
                          cursor: 'pointer',
                          px: 2.5,
                          py: 1,
                          minWidth: 92,
                          borderRadius: '10px',
                          fontFamily: PUBLIC_FORM.font,
                          fontSize: '0.88rem',
                          fontWeight: 700,
                          color: actif ? choix.tone : PUBLIC_FORM.textMuted,
                          backgroundColor: actif ? choix.soft : PUBLIC_FORM.surface,
                          border: `1px solid ${actif ? choix.tone : PUBLIC_FORM.lineStrong}`,
                          transition: 'all 0.15s ease',
                          '&:hover': { borderColor: actif ? choix.tone : PUBLIC_FORM.accent }
                        }}
                      >
                        {choix.label}
                      </Box>
                    );
                  })}
                </Box>

                <TextField
                  value={rep.commentaire || ''}
                  onChange={(e) => poser(rang, 'commentaire', e.target.value)}
                  placeholder="Précision (facultatif)"
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 500 }}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: PUBLIC_FORM.accent,
                      borderWidth: 1
                    }
                  }}
                />
              </Paper>
            );
          })}

          {/* ---------------------------------------------- Signature -- */}
          <Paper elevation={0} sx={{ ...surfaceCard, p: 3, mb: 2 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: PUBLIC_FORM.text, mb: 2 }}>
              Qui remplit cette grille ?
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField
                label="Nom et prénom"
                value={repondant}
                onChange={(e) => setRepondant(e.target.value)}
                size="small"
                required
                sx={{ flex: '1 1 220px', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
              <TextField
                label="Fonction"
                value={fonction}
                onChange={(e) => setFonction(e.target.value)}
                size="small"
                sx={{ flex: '1 1 220px', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Box>
          </Paper>

          {erreur && (
            <Typography sx={{ fontSize: '0.85rem', color: PUBLIC_FORM.danger, mb: 2 }}>
              {erreur}
            </Typography>
          )}

          {/* ------------------------------------------------- Envoi -- */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mt: 3 }}>
            <Button
              type="submit"
              disabled={envoi}
              sx={{
                background: PUBLIC_FORM.cta,
                color: PUBLIC_FORM.surface,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.92rem',
                px: 3.5,
                py: 1.5,
                borderRadius: '10px',
                boxShadow: `0 8px 18px -8px ${PUBLIC_FORM.accent}`,
                '&:hover': { background: PUBLIC_FORM.cta, filter: 'brightness(0.94)' },
                '&:disabled': { background: PUBLIC_FORM.disabledBg, color: PUBLIC_FORM.disabledText, boxShadow: 'none' }
              }}
            >
              {envoi
                ? <CircularProgress size={20} sx={{ color: PUBLIC_FORM.disabledText }} />
                : 'Envoyer la grille'}
            </Button>

            <Typography sx={{ fontSize: '0.75rem', color: PUBLIC_FORM.textFaint }}>
              Vous pourrez revenir corriger vos réponses avec ce même lien.
            </Typography>
          </Box>

          <Typography sx={{ textAlign: 'center', fontSize: '0.75rem', color: PUBLIC_FORM.textFaint, mt: 4 }}>
            Propulsé par Syndays
          </Typography>

        </form>
      </Container>
    </Box>
  );
};

export default EvaluationLieuPublic;
